import flow from 'lodash/flow'
import lowerCase from 'lodash/lowerCase'
import replace from 'lodash/replace'
import startCase from 'lodash/startCase'
import {
  AlertTriggerProperty,
  ApplicantEntityType,
  ApplicationStatus,
  BGSwitchSecurityType,
  BankAssetType,
  BankUserRole,
  CollaboratorRole,
  ContractFormRichTextPrefillOption,
  CreateDraftCollaboratorRole,
  CreditStatus,
  DealStatus,
  DeliveryType,
  DocumentHistoryChangeType,
  DocumentRetentionType,
  DocumentStatus,
  DocumentVariant,
  DraftContractForm,
  DraftOriginationStatus,
  DraftProcessType,
  DraftPurpose,
  DraftRequestedDocumentPurpose,
  DraftStatus,
  EntityAddressState,
  EntityWealthAccumulation,
  FacilityApplicationStatus,
  FacilityType,
  FeeSummaryStatus,
  FinanceType,
  Financier,
  FundingMethod,
  GreenIdVerificationStatus,
  IdentificationApprovalStatus,
  IdentificationCompanyDetailsTaxFatcaStatus,
  IdentificationCompanyDetailsTaxTaxExemptionType,
  IdentificationCompanyDetailsTaxType,
  IdentificationEntityType,
  IdentificationIndustry,
  IdentificationUserRole,
  Industry,
  InvitationStatus,
  MemberRole,
  OrganisationCapability,
  OrganisationStructure,
  OrganisationVariant,
  PartyEntityType,
  PartyVariant,
  PreviewModalType,
  PropertyType,
  RecurringFeeType,
  RequiredDocumentFlow,
  RequiredDocumentType,
  RiskStatus,
  SecurityType,
  fieldNameToRequirementMappingOptions
} from '~/types/graphql/'
export * from './file'
export * from './options'
export * from './referralPortal'
export * from './strings'
export * from './suppliers'
export * from './tables'

export function asReadonly<T>(o: T): Readonly<T> {
  return o as Readonly<T>
}

export const mapEnum = (source: object, valueFormatter?: (value: string) => string) =>
  new Map(Object.entries(source).map(([key, value]) => [key, valueFormatter ? valueFormatter(value) : value]))

export const INDUSTRY_OPTIONS = mapEnum(Industry, startCase)

export const FEE_SUMMARY_STATUS_OPTIONS = mapEnum(FeeSummaryStatus, flow([lowerCase, startCase]))

export const DELIVERY_TYPE_OPTIONS = new Map<DeliveryType, string>([
  [DeliveryType.DIGITAL, 'Digital'],
  [DeliveryType.PHYSICAL, 'Physical'],
  [DeliveryType.DIGITAL_OR_PHYSICAL, 'Both']
])

export const CREDIT_STATUS_OPTIONS = mapEnum(CreditStatus, flow([lowerCase, startCase]))

export const REQUIRED_DOCUMENT_FLOW_OPTIONS = new Map<RequiredDocumentFlow, string>([
  [RequiredDocumentFlow.REQUESTED_FROM_CLIENT, 'Request a document from the client'],
  [RequiredDocumentFlow.SENT_TO_CLIENT, 'Send a document to the client']
])

export const REQUIRED_DOCUMENT_TYPE_OPTIONS = new Map<RequiredDocumentType, string>([
  [RequiredDocumentType.APPLICATION_FORM, 'Application Form'],
  [RequiredDocumentType.CERTIFIED_TRUST_DOCUMENT, 'Certified Trust Document'],
  [RequiredDocumentType.CUSTOMER_IDENTIFICATION, 'Customer Identification'],
  [RequiredDocumentType.DRAFT_BG_FORM, 'Draft Bank Guarantee'],
  [RequiredDocumentType.FOR_CLIENT_RECORD, 'Document For Client Record'],
  [RequiredDocumentType.SHARE_CERTIFICATE, 'Share Certificate'],
  [RequiredDocumentType.OTHER_DOCUMENT, 'Other Document']
])

export const CREATE_DRAFT_COLLABORATOR_ROLE_OPTIONS = new Map<CreateDraftCollaboratorRole, string>([
  [CreateDraftCollaboratorRole.ISSUER, 'Representative of the Applicant'],
  [CreateDraftCollaboratorRole.BENEFICIARY, 'Representative of the Beneficiary'],
  [CreateDraftCollaboratorRole.LIMITED, 'Limited Party']
])

export const DRAFT_KEY_LABELS = new Map<string, string>([
  ['description', 'Description'],
  ['amount', 'Amount'],
  ['expiresAt', 'Expiry Date'],
  ['fundingMethod', 'Funding Method'],
  ['purpose', 'Purpose'],
  ['specifiedOtherPurpose', 'Specified Other Purpose']
])
export const PARTY_KEY_LABELS = new Map<string, string>([
  ['name', 'Name'],
  ['abn', 'ABN/ACN'],
  ['address', 'Address']
])
export const COLLABORATOR_KEY_LABELS = new Map<string, string>([
  ['givenName', 'First Name'],
  ['familyName', 'Last Name'],
  ['collaboratorRoles', 'Roles']
])
export const DRAFT_COLLABORATOR_ROLE_OPTIONS = new Map<CollaboratorRole, string>([
  [CollaboratorRole.OWNER, 'Owner'],
  [CollaboratorRole.ISSUER, 'Issuer'],
  [CollaboratorRole.BENEFICIARY, 'Beneficiary'],
  [CollaboratorRole.LIMITED, 'Limited User']
])
export const MEMBER_ROLE_OPTIONS = new Map<MemberRole, string>([
  [MemberRole.ADMIN, 'Administrator'],
  [MemberRole.USER, 'Standard User'],
  [MemberRole.LIMITED_USER, 'Limited User']
])
export const ASSURO_AND_SUPPLIER_ROLE_OPTIONS = new Map<BankUserRole, string>([
  [BankUserRole.ASSURO_GENERAL, 'Assuro General'],
  [BankUserRole.ASSURO_RELATIONSHIP_MANAGER, 'Assuro Relationship Manager'],
  [BankUserRole.EXTERNAL_SUPPLIER, 'External Supplier']
])
export const BANK_ASSET_TYPES = mapEnum(BankAssetType, flow([lowerCase, startCase]))
export const BANK_USER_ROLE_OPTIONS = mapEnum(BankUserRole, flow([lowerCase, startCase]))
export const ORGANISATION_VARIANT_OPTIONS = mapEnum(OrganisationVariant, flow([lowerCase, startCase]))
export const FUNDING_METHOD_OPTIONS = mapEnum(FundingMethod, flow([lowerCase, startCase]))
export const DOCUMENT_VARIANT_OPTIONS = mapEnum(DocumentVariant, flow([lowerCase, startCase]))
export const FACILITY_APPLICATION_STATUS_OPTIONS = mapEnum(FacilityApplicationStatus, flow([lowerCase, startCase]))
export const FACILITY_TYPE_OPTIONS = new Map<FacilityType, string>([
  [FacilityType.PROPERTY_BACKED, 'Property'],
  [FacilityType.SURETY_BOND, 'Surety Bond']
])

export const FACILITY_DOCUMENT_VARIANT_OPTIONS = new Map(
  [DocumentVariant.BANK_GUARANTEE, DocumentVariant.SURETY_BOND].map(item => [
    item,
    flow([lowerCase, startCase])(item.valueOf())
  ])
)
export const RECURRING_FEE_OPTIONS = mapEnum(RecurringFeeType, flow([lowerCase, startCase]))
export const PARTY_VARIANT_OPTIONS = mapEnum(PartyVariant, flow([lowerCase, startCase]))
export const DOCUMENT_CHANGE_TYPE_OPTIONS = mapEnum(DocumentHistoryChangeType, flow([lowerCase, startCase]))
export const DOCUMENT_STATUS_OPTIONS = mapEnum(
  DocumentStatus,
  flow([lowerCase, (status: DocumentStatus) => replace(status, 'closed', ''), startCase])
)
export const ALERT_TRIGGER_PROPERTY_OPTIONS = mapEnum(AlertTriggerProperty, flow([lowerCase, startCase]))
export const DRAFT_STATUS_OPTIONS = mapEnum(
  DraftStatus,
  flow([lowerCase, (status: DraftStatus) => replace(status, 'closed', ''), startCase])
)
/**
 * Maintain the purpose map, including deprecated values that are still needed for display
 */
const purposeOptionMap: [DraftPurpose, string][] = [
  [DraftPurpose.RENTAL_BOND, 'Lease / Rental bond'],
  [DraftPurpose.SURETY_FOR_BUILDING, 'Security for construction works'],
  [DraftPurpose.DEPOSIT_BOND, 'Deposit bond'],
  [DraftPurpose.SURETY_FOR_COSTS, 'Surety for cost'],
  [DraftPurpose.SURETY_FOR_PERFORMANCE, 'Surety for performance'],
  [DraftPurpose.ENVIRONMENTAL_PERFORMANCE, 'Environmental performance'],
  [DraftPurpose.FINANCIAL_ASSURANCE, 'Financial assurance'],
  [DraftPurpose.SECURITY_DEPOSIT, 'Security deposit'],
  [DraftPurpose.OTHER, 'Other']
]
/**
 * Use for displaying all purpose options, including deprecated purposes.
 */
export const DRAFT_PURPOSE_OPTIONS = new Map<DraftPurpose, string>(purposeOptionMap)
const createDraftPurposeKeys: DraftPurpose[] = [
  DraftPurpose.RENTAL_BOND,
  DraftPurpose.SURETY_FOR_BUILDING,
  DraftPurpose.SURETY_FOR_PERFORMANCE,
  DraftPurpose.SECURITY_DEPOSIT,
  DraftPurpose.OTHER
]
export type CreateDraftPurpose = typeof createDraftPurposeKeys[number]
const createDraftPurposeMap = purposeOptionMap.filter(([key]) => createDraftPurposeKeys.includes(key))
/**
 * Display updated purposes valid for new applications
 */
export const CREATE_DRAFT_PURPOSE_OPTIONS = new Map<CreateDraftPurpose, string>(createDraftPurposeMap)
export const RISK_STATUS_OPTIONS = new Map<RiskStatus, string>([
  [RiskStatus.LOW, 'Low'],
  [RiskStatus.MID, 'Medium'],
  [RiskStatus.HIGH, 'High']
])
export const DOCUMENT_RETENTION_TYPE_OPTIONS = mapEnum(
  DocumentRetentionType,
  flow([lowerCase, (status: DocumentRetentionType) => replace(status, '_', ' '), startCase])
)
export const ORGANISATION_TYPE_OPTIONS = mapEnum(
  OrganisationStructure,
  flow([lowerCase, (status: OrganisationStructure) => replace(status, '_', ' '), startCase])
)
export const ENTITY_WEALTH_ACCUMULATION_OPTIONS = mapEnum(
  EntityWealthAccumulation,
  flow([lowerCase, (status: EntityWealthAccumulation) => replace(status, '_', ' '), startCase])
)
export const IDENTIFICATION_INDUSTRY_OPTIONS = mapEnum(
  IdentificationIndustry,
  flow([lowerCase, (status: IdentificationIndustry) => replace(status, '_', ' '), startCase])
)
export const DOCUMENT_SECURITY_TYPE_OPTIONS = mapEnum(
  SecurityType,
  flow([lowerCase, (status: SecurityType) => replace(status, '_', ' '), startCase])
)
export const BG_SWITCH_SECURITY_TYPE_OPTIONS = mapEnum(
  BGSwitchSecurityType,
  flow([lowerCase, (status: BGSwitchSecurityType) => replace(status, '_', ' '), startCase])
)
export const DRAFT_CONTRACT_FORM_OPTIONS = mapEnum(
  DraftContractForm,
  flow([lowerCase, (status: DraftContractForm) => replace(status, '_', ' '), startCase])
)
export const DRAFT_PROCESS_OPTIONS = mapEnum(
  DraftProcessType,
  flow([lowerCase, (status: DraftProcessType) => replace(status, '_', ' '), startCase])
)
export const DRAFT_REQUESTED_DOCUMENT_OPTIONS = mapEnum(
  DraftRequestedDocumentPurpose,
  flow([lowerCase, (status: DraftRequestedDocumentPurpose) => replace(status, '_', ' '), startCase])
)
export const DRAFT_REQUESTED_DOCUMENT_TEMPLATE_OPTIONS = mapEnum(
  DraftContractForm,
  flow([lowerCase, (status: DraftContractForm) => replace(status, '_', ' '), startCase])
)
export const PARTY_ENTITY_TYPE_OPTIONS = mapEnum(
  PartyEntityType,
  flow([lowerCase, (status: PartyEntityType) => replace(status, '_', ' '), startCase])
)
export const PREVIEW_MODAL_OPTIONS = mapEnum(
  PreviewModalType,
  flow([lowerCase, (status: PreviewModalType) => replace(status, '_', ' '), startCase])
)
export const IDENTIFICATION_ENTITY_TYPE = mapEnum(
  IdentificationEntityType,
  flow([lowerCase, (status: IdentificationEntityType) => replace(status, '_', ' '), startCase])
)
export const IDENTIFICATION_COMPANY_DETAILS_TAX_FATCA = mapEnum(
  IdentificationCompanyDetailsTaxFatcaStatus,
  flow([lowerCase, (status: IdentificationCompanyDetailsTaxFatcaStatus) => replace(status, '_', ' '), startCase])
)

export const GREEN_ID_STATUS = mapEnum(
  GreenIdVerificationStatus,
  flow([lowerCase, (status: GreenIdVerificationStatus) => replace(status, '_', ''), startCase])
)
export const IDENTIFICATION_APPROVAL_STATUS = mapEnum(
  IdentificationApprovalStatus,
  flow([lowerCase, (status: IdentificationApprovalStatus) => replace(status, '_', ''), startCase])
)
export const IDENTIFICATION_COMPANY_DETAILS_TAX_EXEMPTIONS = mapEnum(
  IdentificationCompanyDetailsTaxTaxExemptionType,
  flow([lowerCase, (status: IdentificationCompanyDetailsTaxTaxExemptionType) => replace(status, '_', ' '), startCase])
)
export const DRAFT_PROCESS_TYPE_OPTIONS = mapEnum(
  DraftProcessType,
  flow([lowerCase, (status: DraftProcessType) => replace(status, '_', ' '), startCase])
)
export const DRAFT_INVITATION_STATUES = mapEnum(
  InvitationStatus,
  flow([lowerCase, (status: InvitationStatus) => replace(status, '_', ' '), startCase])
)
export const DRAFT_ORIGINATION_STATUS_OPTIONS = mapEnum(
  DraftOriginationStatus,
  flow([lowerCase, (status: DraftOriginationStatus) => replace(status, 'closed', ''), startCase])
)
export const IDENTIFICATION_APPLICATION_STATUS_OPTIONS = mapEnum(ApplicationStatus, flow([lowerCase, startCase]))

export const IDENTIFICATION_COMPANY_DETAILS_TAX_TYPE_OPTIONS = mapEnum(
  IdentificationCompanyDetailsTaxType,
  flow([lowerCase, (status: IdentificationCompanyDetailsTaxType) => replace(status, '_', ' '), startCase])
)

export const DEAL_STATUS_OPTIONS = mapEnum(DealStatus, flow([lowerCase, startCase]))

export const IDENTIFICATION_COMPANY_DETAILS_TAX_TYPE = {
  ACTIVE_NON_FINANCIAL_ENTITY: 'Australian Non-Financial Entity (NFE)',
  AUSTRALIAN_PUBLIC_LISTED:
    'Australian Public Listed Company, majority owned subsidiary of Australian Public Listed Company or Australian Registered Charity',
  FINANCIAL_INSTITUTION: 'Financial Institution',
  OTHER: 'Other'
}
export const IDENTIFICATION_COMPANY_DETAILS_TAX_STATUS = {
  USE_ABN: 'I wish to provide and authorise the use of a TFN or ABN for this account(s)',
  DO_NOT_WISH_TO_PROVIDE: 'I do not wish to provide a TFN or ABN',
  EXEMPT_FROM_PROVIDING_A_TFN: 'I am exempt from providing a TFN for tax purposes'
}

export const FINANCIAL_INSTITUTIONS = [
  'Commonwealth Bank of Australia',
  'National Australia Bank',
  'ANZ',
  'Westpac',
  'Citi Bank',
  'HSBC',
  'Deutsche Bank',
  'RBS',
  'Bendigo and Adelaide Bank',
  'Bankwest',
  'Suncorp',
  'Macquarie Bank',
  'ME Bank',
  'St George',
  'Judo Bank',
  'Credit Suisse',
  'ABN Amro',
  'ING Bank'
]

export const BOND_PROVIDERS = [
  'Vero',
  'Asset Insure',
  'Bond & Credit Co',
  'Bonded Global Australia',
  'Tokio Marine',
  'QBE',
  'AIG',
  'Swiss Re',
  'Liberty',
  'Other'
]

export const RECURRING_COST_FREQUENCIES = new Map<number, string>([
  [1, 'Monthly'],
  [3, 'Quarterly'],
  [6, 'Biannually'],
  [12, 'Annually'],
  [0, 'Once Off (Upfront)']
])

export const RECURRING_INTEREST_FREQUENCIES = new Map<number, string>([
  [1, 'Monthly'],
  [3, 'Quarterly'],
  [6, 'Biannually'],
  [12, 'Annually'],
  [24, '2 Years'],
  [36, '3 Years'],
  [0, 'End of Term']
])

// User Permissions
export const MANAGE_INVITATIONS = OrganisationCapability.MANAGE_INVITATIONS
export const MANAGE_MEMBERS = OrganisationCapability.MANAGE_MEMBERS
export const UPDATE_DETAILS = OrganisationCapability.UPDATE_DETAILS

// Document Variants
export const BANK_GUARANTEE = DocumentVariant.BANK_GUARANTEE
export const SURETY_BOND = DocumentVariant.SURETY_BOND
export const CASH = DocumentVariant.CASH

// Currency Format
export const CURRENCY_FORMAT_OPTIONS = {
  currency: 'AUD',
  locale: 'en-AU',
  distractionFree: false,
  allowNegative: false,
  valueRange: { min: 1, max: 999999999.99 }
}

export const CURRENCY_FORMAT_OPTIONS_MINZERO = {
  ...CURRENCY_FORMAT_OPTIONS,
  valueRange: { ...CURRENCY_FORMAT_OPTIONS.valueRange, min: 0 }
}

export const PERCENTAGE_FORMAT_OPTIONS = {
  currency: {
    suffix: '%'
  },
  precision: 2,
  distractionFree: false,
  allowNegative: false,
  valueRange: { min: 0.0, max: 100.0 }
}

export const PERCENTAGE_FORMAT_WITH_NEGATIVE_OPTIONS = {
  currency: {
    suffix: '%'
  },
  precision: 2,
  distractionFree: false,
  allowNegative: true,
  valueRange: { min: -100.0, max: 100.0 }
}

export const PERCENTAGE_FORMAT_OPTIONS_LARGE = {
  currency: {
    suffix: '%'
  },
  precision: 2,
  distractionFree: false,
  allowNegative: false,
  valueRange: { min: 0.0, max: 1000.0 }
}

export const ASSURO_NAME = 'Assuro'

export const MaxMetadataFields = 20
export const MaxMetadataFilters = 5

export const documentFacilityAttachmentTypes = [
  {
    title: 'Financial statements',
    variant: 'FINANCIAL_STATEMENTS',
    errors: false,
    description:
      'Consolidated financial statements for last financial year (Including profit & loss; balance sheet) with support commentary. A description of which entities are within the consolidated position.'
  },
  {
    title: 'Management accounts',
    variant: 'MANAGEMENT_ACCOUNTS',
    errors: false,
    description:
      'Most recent management accounts ( including profit and loss; balance sheet) with supporting commentary for the above mentioned entities. A description of which entities are within the consolidated position.'
  },
  {
    title: 'Group Structure',
    variant: 'GROUP_STRUCTURE',
    errors: false,
    description: 'Purpose and the source of revenue of each individual entity.'
  },
  {
    title: 'Banking facilities',
    variant: 'BANKING_FACILITIES',
    errors: false,
    description:
      'A summary of the groups banking facilities, could you please provide current balance, details of security pledged.'
  },
  {
    title: 'Cashflow forecasts',
    variant: 'CASHFLOW_FORECASTS',
    errors: false,
    description: 'Budgeted cashflow forecasts for the next 12 months.'
  },
  {
    title: 'Work in progress',
    variant: 'WORK_IN_PROGRESS',
    errors: false,
    description:
      'Detailed work in progress, detailing project name, project description, contract value, GP margins and revenue to complete.'
  }
]

export const AS2124Wording: ContractFormRichTextPrefillOption = {
  id: 'AS2124',
  contractForm: DraftContractForm.AS2124,
  display: 'AS2124',
  content: `<h1>UNCONDITIONAL UNDERTAKING</h1><p></p><h3>Bond No: --YET TO BE ISSUED--</h3><h3>Bond Type: {{ bondType }}</h3><p></p><p></p><p>At the request of {{ issuerName }} {{ isIssuerABNOrACN }} {{ issuerACN }} of {{ issuerAddress }} (“the Contractor”) and in consideration of {{ beneficiaryName }} {{ isBeneficiaryABNOrACN }} {{ beneficiaryACN }} of {{ beneficiaryAddress }} (“the Principal”) accepting this undertaking in respect of the contract for {{ contractDescription }}(“the Contract”), <strong>Swiss Re International SE</strong> ARBN 138 873 211 (“the Financial Institution”) C/- Assetinsure Pty Limited ACN 066 463 803 of Level 21, 45 Clarence Street, Sydney NSW 2000 unconditionally undertakes to pay on demand any sum or sums which may from time to time be demanded by the Principal to a maximum aggregate sum of {{ contractSumInWords }} (“the Sum”).</p><p></p><p>This undertaking is to continue until:</p><ul><li><p>notification has been received from the Principal that the Sum is no longer required by the Principal; or</p></li><li><p>this undertaking is returned to the Financial Institution; or</p></li><li><p>payment to the Principal by the Financial Institution of the whole of the Sum or such part as the Principal may require.</p></li></ul><p></p><p>Should the Financial Institution be notified in writing, purporting to be signed by a director for and on behalf of the Principal that the Principal desires payment to be made of the whole or any part or parts of the Sum, it is unconditionally agreed that the Financial Institution will make the payment or payments to the Principal forthwith without reference to the Contractor and notwithstanding any notice given by the Contractor not to pay same.</p><p></p><p>Provided always that the Financial Institution may at any time without being required to do so, pay to the</p><p>Principal:</p><ul><li><p>the Sum less any amount or amounts it may previously have paid under this undertaking or</p></li><li><p>such lesser sum as may be required and specified by the Principal</p></li></ul><p></p><p>and thereupon the liability of the Financial Institution hereunder shall immediately cease. </p><p></p><p>The Principal may not transfer, assign or otherwise deal with the benefit of this undertaking without the Financial Institution’s prior written consent, which will not be unreasonably withheld.</p>`
}

export const LegalContentOptions: ContractFormRichTextPrefillOption[] = [
  AS2124Wording,
  {
    id: 'AS2124_WITH_EXPIRY',
    contractForm: DraftContractForm.AS2124_WITH_EXPIRY,
    display: 'AS2124 (with expiry date)',
    content: `<h1>UNCONDITIONAL UNDERTAKING</h1><p></p><h3>Bond No: --YET TO BE ISSUED--</h3><h3>Bond Type: {{ bondType }}</h3><p></p><p></p><p>At the request of {{ issuerName }} {{ isIssuerABNOrACN }} {{ issuerACN }} of {{ issuerAddress }} (“the Contractor”) and in consideration of {{ beneficiaryName }} {{ isBeneficiaryABNOrACN }} {{ beneficiaryACN }} of {{ beneficiaryAddress }} (“the Principal”) accepting this undertaking in respect of the contract for {{ contractDescription }}(“the Contract”), <strong>Swiss Re International SE</strong> ARBN 138 873 211 (“the Financial Institution”) C/- Assetinsure Pty Limited ACN 066 463 803 of Level 21, 45 Clarence Street, Sydney NSW 2000 unconditionally undertakes to pay on demand any sum or sums which may from time to time be demanded by the Principal to a maximum aggregate sum of {{ contractSumInWords }} (“the Sum”).</p><p></p><p>This undertaking is to continue until:</p><ul><li><p>notification has been received from the Principal that the Sum is no longer required by the Principal; or</p></li><li><p>this undertaking is returned to the Financial Institution; or</p></li><li><p>payment to the Principal by the Financial Institution of the whole of the Sum or such part as the Principal may require; or</p></li><li><p>4.00pm on {{ expiryDate }} (“Expiry Date”).</p></li></ul><p></p><p>Should the Financial Institution be notified in writing, purporting to be signed by a director for and on behalf of the Principal that the Principal desires payment to be made of the whole or any part or parts of the Sum, it is unconditionally agreed that the Financial Institution will make the payment or payments to the Principal forthwith without reference to the Contractor and notwithstanding any notice given by the Contractor not to pay same.</p><p></p><p>Provided always that the Financial Institution may at any time without being required to do so, pay to the</p><p>Principal:</p><ul><li><p>the Sum less any amount or amounts it may previously have paid under this undertaking or</p></li><li><p>such lesser sum as may be required and specified by the Principal</p></li></ul><p></p><p>and thereupon the liability of the Financial Institution hereunder shall immediately cease. </p><p></p><p>The Principal may not transfer, assign or otherwise deal with the benefit of this undertaking without the Financial Institution’s prior written consent, which will not be unreasonably withheld.</p>`
  },
  {
    id: 'AS4000',
    contractForm: DraftContractForm.AS4000,
    display: 'AS4000',
    content: `<h1>UNCONDITIONAL UNDERTAKING</h1><p></p><h3>Bond No: --YET TO BE ISSUED--</h3><h3>Bond Type: {{ bondType }}</h3><p></p><p></p><p>At the request of {{ issuerName }} {{ isIssuerABNOrACN }} {{ issuerACN }} of {{ issuerAddress }} (“the Contractor”) and in consideration of {{ beneficiaryName }} {{ isBeneficiaryABNOrACN }} {{ beneficiaryACN }} of {{ beneficiaryAddress }} (“the Principal”) accepting this undertaking in respect of the contract for the {{ contractDescription }} (“the Contract”), <strong>Swiss Re International SE</strong> ARBN 138 873 211 (“the Financial Institution”) C/- Assetinsure Pty Limited ACN 066 463 803 of Level 21, 45 Clarence Street, Sydney NSW 2000 unconditionally undertakes to pay on demand any sum or sums which may from time to time be demanded by the Principal to a maximum aggregate sum of {{ contractSumInWords }} <strong>(\${{ contractSum }})</strong> (“the Sum”).</p><p></p><p>This undertaking is to continue until notification has been received from the Principal that the Sum is no longer required by the Principal or until this undertaking is returned to the Financial Institution or until payment to the Principal by the Financial Institution of the whole of the Sum or such part as the Principal may require.</p><p></p><p>Should the Financial Institution be notified in writing, purporting to be signed by for and on behalf of the Principal that the Principal desires payment to be made of the whole or any part or parts of the Sum, it is unconditionally agreed that the Financial Institution will make the payment or payments to the Principal forthwith without reference to the Contractor and notwithstanding any notice given by the Contractor not to pay same.</p><p></p><p>Provided always that the Financial Institution may at any time without being required so to do pay to the Principal the sum of {{ contractSumInWords }} <strong>(\${{ contractSum }})</strong> less any amount or amounts it may previously have paid under this undertaking or such lesser sum as may be required and specified by the Principal and thereupon the liability of the Financial Institution hereunder shall immediately cease.</p><p></p><p>The Principal may not transfer, assign or otherwise deal with the benefit of this undertaking without the Financial Institution’s prior written consent which will not be unreasonably withheld.</p>`
  },
  {
    id: 'AS4000_WITH_EXPIRY',
    contractForm: DraftContractForm.AS4000_WITH_EXPIRY,
    display: 'AS4000 (with expiry date)',
    content: `<h1>UNCONDITIONAL UNDERTAKING</h1><p></p><h3>Bond No: --YET TO BE ISSUED--</h3><h3>Bond Type: {{ bondType }}</h3><p></p><p></p><p>At the request of {{ issuerName }} {{ isIssuerABNOrACN }} {{ issuerACN }} of {{ issuerAddress }} (“the Contractor”) and in consideration of {{ beneficiaryName }} {{ isBeneficiaryABNOrACN }} {{ beneficiaryACN }} of {{ beneficiaryAddress }} (“the Principal”) accepting this undertaking in respect of the contract for the {{ contractDescription }} (“the Contract”), <strong>Swiss Re International SE</strong> ARBN 138 873 211 (“the Financial Institution”) C/- Assetinsure Pty Limited ACN 066 463 803 of Level 21, 45 Clarence Street, Sydney NSW 2000 unconditionally undertakes to pay on demand any sum or sums which may from time to time be demanded by the Principal to a maximum aggregate sum of {{ contractSumInWords }} <strong>(\${{ contractSum }})</strong> (“the Sum”).</p><p></p><p>This undertaking is to continue until notification has been received from the Principal that the Sum is no longer required by the Principal or until this undertaking is returned to the Financial Institution or until payment to the Principal by the Financial Institution of the whole of the Sum or such part as the Principal may require, or 4.00pm on {{ expiryDate }}; whichever occurs first.</p><p></p><p>Should the Financial Institution be notified in writing, purporting to be signed by for and on behalf of the Principal that the Principal desires payment to be made of the whole or any part or parts of the Sum, it is unconditionally agreed that the Financial Institution will make the payment or payments to the Principal forthwith without reference to the Contractor and notwithstanding any notice given by the Contractor not to pay same.</p><p></p><p>Provided always that the Financial Institution may at any time without being required so to do pay to the Principal the sum of {{ contractSumInWords }} <strong>(\${{ contractSum }})</strong> less any amount or amounts it may previously have paid under this undertaking or such lesser sum as may be required and specified by the Principal and thereupon the liability of the Financial Institution hereunder shall immediately cease.</p><p></p><p>The Principal may not transfer, assign or otherwise deal with the benefit of this undertaking without the Financial Institution’s prior written consent which will not be unreasonably withheld.</p>`
  }
]

export const DefaultLegalContentOption: ContractFormRichTextPrefillOption = AS2124Wording

export const fieldNameToRequirementMapping: fieldNameToRequirementMappingOptions = {
  BANK_FRONTED_BONDS_REQUESTED: { name: 'bankFrontedBondsRequested', attachment: false },
  BANK_FRONTED_BONDS_REQUESTED_REASON: { name: 'bankFrontedBondsRequestedReason', attachment: false },
  DEFECTS_AND_FAULTS: { name: 'defectsAndFaults', attachment: false },
  DEFECTS_AND_FAULTS_DETAILS: { name: 'defectsAndFaults', attachment: false },
  DEFECTS_LIABILITY_PERIOD_EXTENDED: { name: 'defectsLiabilityPeriodExtended', attachment: false },
  DEFECTS_LIABILITY_PERIOD_EXTENDED_DETAILS: { name: 'defectsLiabilityPeriodExtended', attachment: false },
  HAS_PREVIOUS_CONTRACTS: { name: 'hasPreviousContracts', attachment: false },
  HAS_SIMILAR_CONTRACTS: { name: 'hasSimilarContracts', attachment: false },
  HAS_DELAY_EVENTS: { name: 'hasDelayEvents', attachment: false },
  HAS_DESIGN_OBLIGATIONS_LIABILITY: { name: 'hasDesignObligationsLiability', attachment: false },
  IS_SUBCONTRACTOR: { name: 'isSubcontractor', attachment: false },
  IS_EXISTING_PROJECT: { name: 'isExistingProject', attachment: false },
  ISSUES_AND_DISPUTES: { name: 'issuesAndDisputes', attachment: false },
  ISSUES_AND_DISPUTES_DETAILS: { name: 'issuesAndDisputes', attachment: false },
  REPLACING_EXISTING_SECURITY: { name: 'replacingExistingSecurity', attachment: false },
  REPLACING_EXISTING_SECURITY_DETAILS: { name: 'replacingExistingSecurity', attachment: false },
  PROJECT_NOT_ON_BUDGET: { name: 'projectNotOnBudget', attachment: false },
  OFFSITE_MATERIALS_EXCERPT_ATTACHMENT: { name: 'OFFSITE_MATERIALS_EXCERPT', attachment: true },
  OFFSITE_MATERIALS_BILLING_ADVICE_OR_PURCHASE_ORDER_ATTACHMENT: {
    name: 'OFFSITE_MATERIALS_BILLING_ADVICE_OR_PURCHASE_ORDER',
    attachment: true
  },
  BENEFICIARY_ABN_OR_ACN: { name: 'abn', attachment: false },
  BENEFICIARY_NAME: { name: 'name', attachment: false },
  BENEFICIARY_ADDRESS: { name: 'address', attachment: false },
  ISSUER_ABN_OR_ACN: { name: 'abn', attachment: false },
  ISSUER_NAME: { name: 'name', attachment: false },
  ISSUER_ADDRESS: { name: 'address', attachment: false }
}

export const AUTH_CONSTANTS = {
  UserLambdaValidationExceptionCode: 'UserLambdaValidationException',
  PreSignUpMessagePrefix: 'PreSignUp failed with error ',
  RestartOauthCode: 'ACCOUNT_LINKED_RESTART_AUTH.'
}

export const COUNTRIES_LIST = asReadonly({
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AF: 'Afghanistan',
  AG: 'Antigua and Barbuda',
  AI: 'Anguilla',
  AL: 'Albania',
  AM: 'Armenia',
  AO: 'Angola',
  AQ: 'Antarctica',
  AR: 'Argentina',
  AS: 'American Samoa',
  AT: 'Austria',
  AU: 'Australia',
  AW: 'Aruba',
  AX: 'Åland Islands',
  AZ: 'Azerbaijan',
  BA: 'Bosnia and Herzegovina',
  BB: 'Barbados',
  BD: 'Bangladesh',
  BE: 'Belgium',
  BF: 'Burkina Faso',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BI: 'Burundi',
  BJ: 'Benin',
  BL: 'Saint Barthélemy',
  BM: 'Bermuda',
  BN: 'Brunei Darussalam',
  BO: 'Bolivia',
  BQ: 'Caribbean Netherlands',
  BR: 'Brazil',
  BS: 'Bahamas',
  BT: 'Bhutan',
  BV: 'Bouvet Island',
  BW: 'Botswana',
  BY: 'Belarus',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Cocos (Keeling) Islands',
  CD: 'Congo:Democratic Republic of',
  CF: 'Central African Republic',
  CG: 'Congo',
  CH: 'Switzerland',
  CI: "Côte d'Ivoire",
  CK: 'Cook Islands',
  CL: 'Chile',
  CM: 'Cameroon',
  CN: 'China',
  CO: 'Colombia',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cape Verde',
  CW: 'Curaçao',
  CX: 'Christmas Island',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DE: 'Germany',
  DJ: 'Djibouti',
  DK: 'Denmark',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  DZ: 'Algeria',
  EC: 'Ecuador',
  EE: 'Estonia',
  EG: 'Egypt',
  EH: 'Western Sahara',
  ER: 'Eritrea',
  ES: 'Spain',
  ET: 'Ethiopia',
  FI: 'Finland',
  FJ: 'Fiji',
  FK: 'Falkland Islands',
  FM: 'Micronesia:Federated States of',
  FO: 'Faroe Islands',
  FR: 'France',
  GA: 'Gabon',
  GB: 'United Kingdom',
  GD: 'Grenada',
  GE: 'Georgia',
  GF: 'French Guiana',
  GG: 'Guernsey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Greenland',
  GM: 'Gambia',
  GN: 'Guinea',
  GP: 'Guadeloupe',
  GQ: 'Equatorial Guinea',
  GR: 'Greece',
  GS: 'South Georgia and the South Sandwich Islands',
  GT: 'Guatemala',
  GU: 'Guam',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HM: 'Heard and McDonald Islands',
  HN: 'Honduras',
  HR: 'Croatia',
  HT: 'Haiti',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IM: 'Isle of Man',
  IN: 'India',
  IO: 'British Indian Ocean Territory',
  IQ: 'Iraq',
  IR: 'Iran',
  IS: 'Iceland',
  IT: 'Italy',
  JE: 'Jersey',
  JM: 'Jamaica',
  JO: 'Jordan',
  JP: 'Japan',
  KE: 'Kenya',
  KG: 'Kyrgyzstan',
  KH: 'Cambodia',
  KI: 'Kiribati',
  KM: 'Comoros',
  KN: 'Saint Kitts and Nevis',
  KP: 'North Korea',
  KR: 'South Korea',
  KW: 'Kuwait',
  KY: 'Cayman Islands',
  KZ: 'Kazakhstan',
  LA: "Lao People's Democratic Republic",
  LB: 'Lebanon',
  LC: 'Saint Lucia',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  LY: 'Libya',
  MA: 'Morocco',
  MC: 'Monaco',
  MD: 'Moldova',
  ME: 'Montenegro',
  MF: 'Saint-Martin (France)',
  MG: 'Madagascar',
  MH: 'Marshall Islands',
  MK: 'Macedonia',
  ML: 'Mali',
  MM: 'Myanmar',
  MN: 'Mongolia',
  MO: 'Macau',
  MP: 'Northern Mariana Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MS: 'Montserrat',
  MT: 'Malta',
  MU: 'Mauritius',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexico',
  MY: 'Malaysia',
  MZ: 'Mozambique',
  NA: 'Namibia',
  NC: 'New Caledonia',
  NE: 'Niger',
  NF: 'Norfolk Island',
  NG: 'Nigeria',
  NI: 'Nicaragua',
  NL: 'The Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'New Zealand',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Peru',
  PF: 'French Polynesia',
  PG: 'Papua New Guinea',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PM: 'St. Pierre and Miquelon',
  PN: 'Pitcairn',
  PR: 'Puerto Rico',
  PS: 'Palestinian Territory:Occupied',
  PT: 'Portugal',
  PW: 'Palau',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'Reunion',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russian Federation',
  RW: 'Rwanda',
  SA: 'Saudi Arabia',
  SB: 'Solomon Islands',
  SC: 'Seychelles',
  SD: 'Sudan',
  SE: 'Sweden',
  SG: 'Singapore',
  SH: 'Saint Helena',
  SI: 'Slovenia',
  SJ: 'Svalbard and Jan Mayen Islands',
  SK: 'Slovakia (Slovak Republic)',
  SL: 'Sierra Leone',
  SM: 'San Marino',
  SN: 'Senegal',
  SO: 'Somalia',
  SR: 'Suriname',
  SS: 'South Sudan',
  ST: 'Sao Tome and Principe',
  SV: 'El Salvador',
  SX: 'Saint-Martin (Pays-Bas)',
  SY: 'Syria',
  SZ: 'Swaziland',
  TC: 'Turks and Caicos Islands',
  TD: 'Chad',
  TF: 'French Southern Territories',
  TG: 'Togo',
  TH: 'Thailand',
  TJ: 'Tajikistan',
  TK: 'Tokelau',
  TL: 'Timor-Leste',
  TM: 'Turkmenistan',
  TN: 'Tunisia',
  TO: 'Tonga',
  TR: 'Turkey',
  TT: 'Trinidad and Tobago',
  TV: 'Tuvalu',
  TW: 'Taiwan',
  TZ: 'Tanzania',
  UA: 'Ukraine',
  UG: 'Uganda',
  UM: 'United States Minor Outlying Islands',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VA: 'Vatican',
  VC: 'Saint Vincent and the Grenadines',
  VE: 'Venezuela',
  VG: 'Virgin Islands (British)',
  VI: 'Virgin Islands (U.S.)',
  VN: 'Vietnam',
  VU: 'Vanuatu',
  WF: 'Wallis and Futuna Islands',
  WS: 'Samoa',
  YE: 'Yemen',
  YT: 'Mayotte',
  ZA: 'South Africa',
  ZM: 'Zambia',
  ZW: 'Zimbabwe'
})

export const IDENTIFICATION_USER_ROLE_OPTIONS = new Map<string, any>([
  [IdentificationUserRole.DIRECTOR, 'Director'],
  [IdentificationUserRole.COMPANY_SECRETARY, 'Company secretary'],
  [IdentificationUserRole.POWER_OF_ATTORNEY, 'Power of attorney'],
  [IdentificationUserRole.ACCOUNTANT, 'Accountant'],
  [IdentificationUserRole.APPOINTER, 'Appointer'],
  [IdentificationUserRole.AUTHORISED_SIGNATORY, 'Authorised signatory'],
  [IdentificationUserRole.BENEFICIAL_OWNER, 'Beneficial owner'],
  [IdentificationUserRole.BENEFICIARY, 'Beneficiary'],
  [IdentificationUserRole.BUSINESS_PARTNER, 'Business partner'],
  [IdentificationUserRole.CEO, 'CEO'],
  [IdentificationUserRole.CFO, 'CFO'],
  [IdentificationUserRole.CHAIRMAN, 'Chairman'],
  [IdentificationUserRole.COBORROWER, 'Coborrower'],
  [IdentificationUserRole.COO, 'COO'],
  [IdentificationUserRole.GUARANTOR, 'Guarantor'],
  [IdentificationUserRole.GUARDIAN, 'Guardian'],
  [IdentificationUserRole.PARTNER, 'Partner'],
  [IdentificationUserRole.PRESIDENT, 'President'],
  [IdentificationUserRole.SECRETARY, 'Secretary'],
  [IdentificationUserRole.SETTLOR, 'Settlor'],
  [IdentificationUserRole.SUPPORT_STAFF, 'Support staff'],
  [IdentificationUserRole.THIRD_PARTY_AUTHORITY, 'Third party authority'],
  [IdentificationUserRole.TREASURER, 'Treasurer'],
  [IdentificationUserRole.TRUSTEE, 'Trustee'],
  [IdentificationUserRole.SOLE_TRADER, 'Sole Trader'],
  [IdentificationUserRole.INDIVIDUAL, 'Individual'],
  [IdentificationUserRole.OTHER, 'Other']
])

export const financeTypeOptions = new Map<FinanceType, string>([
  [FinanceType.WORKING_CAPITAL, 'Working Capital'],
  [FinanceType.EQUIPMENT_OR_ASSET_FINANCE, 'Equipment or Asset Finance'],
  [FinanceType.LINE_OF_CREDIT, 'Line of Credit'],
  [FinanceType.OTHER, 'Other']
])

export const financierOptions = new Map<Financier, string>([
  [Financier.VALIANT, 'Valiant'],
  [Financier.PROSPA, 'Prospa']
])

export const AUSTRALIAN_STATE_OPTIONS = mapEnum(EntityAddressState)
export const PROPERTY_TYPE_OPTIONS = mapEnum(
  PropertyType,
  flow([lowerCase, (status: PartyEntityType) => replace(status, '_', ' '), startCase])
)

export const APPLICANT_ENTITY_TYPE_OPTIONS = mapEnum(ApplicantEntityType, flow([lowerCase, startCase]))

export enum AdditionalInfoReferences {
  // 0 means don't show a reference
  NO_REFERENCE = 0,
  REFERENCE_ONE = 1,
  REFERENCE_TWO = 2,
  REFERENCE_THREE = 3,
  REFERENCE_FOUR = 4
}
